import { Stethoscope02Icon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { api } from "@slapinc/slappos-backend/api";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery } from "convex/react";
import { LayoutPanelTop } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { Can } from "@/components/can";
import { PageContent } from "@/components/layout/page-content";
import { CreatePrescriberModal } from "@/components/modals/create-prescriber-modal";
import { EditPrescriberModal } from "@/components/modals/edit-prescriber-modal";
import { ActionHeaderButton } from "@/components/ui/action-header-button";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { DisplaySelect } from "@/components/ui/display-select";
import { EmptyStateWithSkeleton } from "@/components/ui/empty-state-with-skeleton";
import { PrescribersTable } from "@/features/prescribers/_components/prescribers-table";
import { useLocalStorageSettings } from "@/hooks/use-local-storage-settings";
import { useMe } from "@/hooks/use-me";
import { handleConvexError } from "@/lib/errors";
import type { PrescriberInput } from "@/lib/schemas/prescriber";
import { toast } from "@/lib/toast";
import type { Prescriber } from "@/lib/types/prescriber";

export const Route = createFileRoute("/(dashboard)/prescribers")({
	component: PrescribersPage,
});

interface DisplaySettings {
	viewMode: "cards" | "rows";
	showHeaders: boolean;
	sortKey: string;
	columnVisibility: Record<string, boolean>;
}

const DEFAULT_SETTINGS: DisplaySettings = {
	viewMode: "rows",
	showHeaders: true,
	sortKey: "date",
	columnVisibility: {
		name: true,
		licenseNo: true,
		type: true,
		address: true,
	},
};

function PrescribersPage() {
	// Get current user to find active organization
	const me = useMe();
	const orgId = me?.activeOrganizationId;

	const {
		settings: displaySettings,
		hasChanges,
		handleReset,
		handleSetDefault,
		updateSetting,
		updateColumnVisibility,
	} = useLocalStorageSettings<DisplaySettings>(
		"slappos_prescribers_display_settings",
		DEFAULT_SETTINGS,
		() => {
			const defaultMode =
				globalThis.window !== undefined && globalThis.innerWidth < 640
					? "cards"
					: "rows";
			return { ...DEFAULT_SETTINGS, viewMode: defaultMode };
		},
	);

	// Modal state
	const [createModalOpen, setCreateModalOpen] = useState(false);
	const [editModalOpen, setEditModalOpen] = useState(false);
	const [selectedPrescriber, setSelectedPrescriber] =
		useState<Prescriber | null>(null);
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [prescriberToDelete, setPrescriberToDelete] =
		useState<Prescriber | null>(null);

	// Mutations
	const createPrescriber = useMutation(api.features.prescribers.web.create);
	const updatePrescriber = useMutation(api.features.prescribers.web.update);
	const deletePrescriber = useMutation(api.features.prescribers.web.remove);

	// Mutation loading states
	const [isCreating, setIsCreating] = useState(false);
	const [isUpdating, setIsUpdating] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);

	// Query prescribers
	const prescribersQuery = useQuery(
		api.features.prescribers.web.list,
		orgId ? { orgId } : "skip",
	);

	const prescribers = (prescribersQuery ?? []) as Prescriber[];
	const isLoading = prescribersQuery === undefined;

	// Sort prescribers
	const sortedPrescribers = useMemo(() => {
		return [...prescribers].sort((a, b) => {
			if (displaySettings.sortKey === "date") {
				return b._creationTime - a._creationTime;
			}
			if (displaySettings.sortKey === "name") {
				return a.name.localeCompare(b.name);
			}
			if (displaySettings.sortKey === "license") {
				return (a.licenseNo || "").localeCompare(b.licenseNo || "");
			}
			return 0;
		});
	}, [prescribers, displaySettings.sortKey]);

	// No pagination, show all sorted results
	const prescribersToShow = sortedPrescribers;

	// Handlers
	const handleEdit = useCallback((prescriber: Prescriber) => {
		setSelectedPrescriber(prescriber);
		setEditModalOpen(true);
	}, []);

	const handleDelete = useCallback((prescriber: Prescriber) => {
		setPrescriberToDelete(prescriber);
		setDeleteDialogOpen(true);
	}, []);

	const confirmDelete = useCallback(async () => {
		if (!prescriberToDelete || !orgId) return;

		setIsDeleting(true);
		try {
			await deletePrescriber({ orgId, prescriberId: prescriberToDelete._id });
			toast.success(<>Prescriber deleted</>);
			setDeleteDialogOpen(false);
			setPrescriberToDelete(null);
		} catch (error) {
			handleConvexError(error, "Failed to delete prescriber");
		} finally {
			setIsDeleting(false);
		}
	}, [prescriberToDelete, orgId, deletePrescriber]);

	const handleCreatePrescriber = useCallback(
		async (data: PrescriberInput) => {
			if (!orgId) return;

			setIsCreating(true);
			try {
				await createPrescriber({
					orgId,
					name: data.name,
					licenseNo: data.licenseNo,
					type: data.type,
					address: data.address,
				});
				toast.success(<>Prescriber created</>);
			} catch (error) {
				handleConvexError(error, "Failed to create prescriber");
				throw error;
			} finally {
				setIsCreating(false);
			}
		},
		[orgId, createPrescriber],
	);

	const handleUpdatePrescriber = useCallback(
		async (data: PrescriberInput) => {
			if (!orgId || !selectedPrescriber) return;

			setIsUpdating(true);
			try {
				await updatePrescriber({
					orgId,
					prescriberId: selectedPrescriber._id,
					name: data.name,
					licenseNo: data.licenseNo,
					type: data.type,
					address: data.address,
				});
				toast.success(<>Prescriber updated</>);
			} catch (error) {
				handleConvexError(error, "Failed to update prescriber");
				throw error;
			} finally {
				setIsUpdating(false);
			}
		},
		[orgId, selectedPrescriber, updatePrescriber],
	);

	return (
		<PageContent
			title="Prescribers"
			controls={
				<Can scope="prescribers.create">
					<ActionHeaderButton
						label="Add Prescriber"
						onClick={() => setCreateModalOpen(true)}
					/>
				</Can>
			}
		>
			<div className="flex flex-1 flex-col gap-4 sm:gap-6">
				{!isLoading && prescribers.length > 0 && (
					<div className="flex flex-col gap-3 sm:flex-row sm:items-center">
						<DisplaySelect
							viewMode={displaySettings.viewMode}
							onViewModeChange={(mode) => updateSetting("viewMode", mode)}
							selectedSort={displaySettings.sortKey}
							onSortChange={(sort) => updateSetting("sortKey", sort)}
							sortOptions={[
								{ value: "date", label: "Date (Newest)" },
								{ value: "name", label: "Name (A-Z)" },
								{ value: "license", label: "License No." },
							]}
							options={[
								{
									key: "showHeaders",
									label: "Show table headers",
									checked: displaySettings.showHeaders,
									icon: <LayoutPanelTop className="size-4" />,
								},
							]}
							onOptionsChange={(key, checked) => {
								if (key === "showHeaders")
									updateSetting("showHeaders", checked);
							}}
							properties={[
								{
									key: "licenseNo",
									label: "License No",
									visible: displaySettings.columnVisibility.licenseNo,
								},
								{
									key: "type",
									label: "Type",
									visible: displaySettings.columnVisibility.type,
								},
								{
									key: "address",
									label: "Address",
									visible: displaySettings.columnVisibility.address,
								},
							]}
							onPropertiesChange={updateColumnVisibility}
							onReset={handleReset}
							onSetDefault={handleSetDefault}
							showFooter={hasChanges}
						/>
					</div>
				)}

				{!isLoading && prescribers.length === 0 ? (
					<div className="flex flex-1 flex-col justify-end pb-12">
						<EmptyStateWithSkeleton
							title="No prescribers found"
							description="Create your first prescriber to manage and track prescriptions more effectively"
							actionLabel="Add Prescriber"
							onAction={() => setCreateModalOpen(true)}
							icon={
								<HugeiconsIcon
									icon={Stethoscope02Icon}
									size={20}
									strokeWidth={2.2}
									className="text-neutral-700"
								/>
							}
						/>
					</div>
				) : (
					<PrescribersTable
						data={prescribersToShow}
						loading={isLoading}
						rowCount={sortedPrescribers.length}
						onEdit={handleEdit}
						onDelete={handleDelete}
						viewMode={displaySettings.viewMode}
						showHeaders={displaySettings.showHeaders}
						columnVisibility={displaySettings.columnVisibility}
						showPagination={false}
					/>
				)}
			</div>

			{/* Create Modal */}
			<CreatePrescriberModal
				open={createModalOpen}
				onOpenChange={setCreateModalOpen}
				onSubmit={handleCreatePrescriber}
				isSubmitting={isCreating}
			/>

			{/* Edit Modal */}
			<EditPrescriberModal
				open={editModalOpen}
				onOpenChange={setEditModalOpen}
				prescriber={selectedPrescriber}
				onSubmit={handleUpdatePrescriber}
				isSubmitting={isUpdating}
			/>

			{/* Delete Confirmation Dialog */}
			<AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Delete prescriber?</AlertDialogTitle>
						<AlertDialogDescription>
							Are you sure you want to delete "{prescriberToDelete?.name}"? This
							action cannot be undone.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
						<AlertDialogAction
							variant="destructive"
							onClick={confirmDelete}
							disabled={isDeleting}
						>
							{isDeleting ? "Deleting..." : "Delete"}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</PageContent>
	);
}
